// Tiện ích DOM cùng giọng với src/panel/panel.ts: h(), clear(), gen() cho renderer async, toast, hộp xác nhận.
export type Child = Node | string | number | null | undefined | false | Child[];

export function h<K extends keyof HTMLElementTagNameMap>(tag: K, attrs: Record<string, unknown> = {}, ...children: Child[]): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (v === undefined || v === null || v === false) continue;
    if (k === "class") el.className = String(v);
    else if (k.startsWith("on") && typeof v === "function") el.addEventListener(k.slice(2).toLowerCase(), v as EventListener);
    else if (k === "disabled" || k === "checked" || k === "hidden" || k === "open" || k === "selected") (el as unknown as Record<string, unknown>)[k] = v;
    else if (k === "value") (el as unknown as HTMLInputElement).value = String(v);
    else if (k === "dataset") for (const [dk, dv] of Object.entries(v as Record<string, string>)) el.dataset[dk] = dv;
    else el.setAttribute(k, String(v));
  }
  append(el, children);
  return el;
}

function append(el: Node, children: Child[]): void {
  for (const c of children) {
    if (c === null || c === undefined || c === false) continue;
    if (Array.isArray(c)) append(el, c);
    else el.appendChild(typeof c === "string" || typeof c === "number" ? document.createTextNode(String(c)) : c);
  }
}

export function svg<K extends keyof SVGElementTagNameMap>(tag: K, attrs: Record<string, string | number> = {}, ...children: (Node | string)[]): SVGElementTagNameMap[K] {
  const el = document.createElementNS("http://www.w3.org/2000/svg", tag);
  for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, String(v));
  for (const c of children) el.appendChild(typeof c === "string" ? document.createTextNode(c) : c);
  return el;
}

export function clear(el: Element): void {
  while (el.firstChild) el.removeChild(el.firstChild);
}

export function $(id: string): HTMLElement {
  const el = document.getElementById(id);
  if (!el) throw new Error(`Không thấy #${id}`);
  return el;
}

// Renderer async: xoá rồi await rồi vẽ → hai lượt chồng nhau sẽ vẽ trùng. Mỗi lượt lấy một số thế hệ; lượt cũ dừng sau await.
const renderGen: Record<string, number> = {};
export function gen(name: string): () => boolean {
  const g = (renderGen[name] = (renderGen[name] ?? 0) + 1);
  return () => renderGen[name] === g;
}

// Thông báo XẾP CHỒNG (tối đa 3 cái gần nhất, mỗi cái hết hạn riêng). Trước đây một ô duy nhất: notice nền (vd "Đã ghi 1 nơi
// vừa làm xong") tới ngay sau khi bấm Duyệt tất cả là ghi đè mất "Đã duyệt 2 lần chạy" — người dùng không thấy kết quả việc vừa bấm.
const TOAST_MAX = 3;
function toastBox(t: HTMLElement): { list: HTMLElement } {
  let list = t.querySelector<HTMLElement>(":scope > .toast-list");
  if (!list) {
    clear(t);
    list = h("div", { class: "toast-list" });
    t.append(list, h("button", { type: "button", class: "toast-x", "aria-label": "Ẩn thông báo", title: "Ẩn mọi thông báo đang hiện", onclick: () => { clear(list!); t.hidden = true; } }, "×"));
  }
  return { list };
}
/** `ms` = thời gian hiện (mặc định 3,5 giây, lỗi 7 giây); thông báo quan trọng như báo cáo/cảnh báo hiện lâu hơn. */
export function toast(text: string, kind: "info" | "err" | "warn" = "info", ms?: number): void {
  const t = document.getElementById("toast");
  if (!t) return;
  const { list } = toastBox(t);
  // Chữ luôn là textNode (thông điệp có thể chứa tên bot/lỗi từ máy chủ).
  const item = h("div", { class: `toast-item ${kind}` }, h("span", { class: "toast-text" }, text));
  list.append(item);
  while (list.children.length > TOAST_MAX) list.firstElementChild!.remove();
  t.className = kind; // lớp theo thông báo mới nhất
  t.hidden = false;
  window.setTimeout(() => {
    item.remove();
    const last = list.lastElementChild as HTMLElement | null;
    if (!last) t.hidden = true;
    else t.className = last.classList.contains("err") ? "err" : last.classList.contains("warn") ? "warn" : "info";
  }, ms ?? (kind === "err" ? 7000 : 3500));
}

export function fail(e: unknown): void {
  toast(e instanceof Error ? e.message || "Lỗi không rõ" : String(e), "err");
}

/** Hộp xác nhận tự vẽ (không dùng window.confirm để E2E bấm được và có chữ tiếng Việt). `nhac` = một dòng nhắc riêng (không chặn), vd
 *  "Chưa gắn sản phẩm — bình luận sẽ không quảng bá sản phẩm" ở hộp Chạy thật (05/10/2026). */
export function confirmBox(opts: { title: string; text?: string; nhac?: string; okText?: string; okTitle?: string; danger?: boolean; cancelText?: string }): Promise<boolean> {
  return new Promise((resolve) => {
    const done = (v: boolean) => { wrap.remove(); resolve(v); };
    const wrap = h("div", { class: "modal-wrap", role: "dialog", "aria-modal": "true", "data-testid": "confirm" },
      h("div", { class: "modal" },
        h("h3", {}, opts.title),
        opts.text ? h("p", { class: "wrap" }, opts.text) : null,
        opts.nhac ? h("p", { class: "wrap modal-nhac", "data-testid": "confirm-nhac" }, opts.nhac) : null,
        h("div", { class: "row end" },
          h("button", { type: "button", title: "Đóng hộp này, không làm gì cả", onclick: () => done(false), "data-testid": "confirm-cancel" }, opts.cancelText ?? "Huỷ"),
          h("button", { type: "button", class: opts.danger ? "danger" : "pri", title: opts.okTitle ?? (opts.danger ? "Xác nhận thực hiện — việc này không hoàn tác được" : "Xác nhận thực hiện"), onclick: () => done(true), "data-testid": "confirm-ok" }, opts.okText ?? "Đồng ý"))));
    wrap.addEventListener("keydown", (e) => { if (e.key === "Escape") done(false); });
    wrap.addEventListener("dong-hop", () => resolve(false)); // L-82: đổi trang / Esc toàn cục → coi như Huỷ
    document.body.appendChild(wrap);
    (wrap.querySelector("button") as HTMLButtonElement | null)?.focus();
  });
}

/** Hộp thông tin tự vẽ với nội dung tuỳ ý. */
export function infoBox(title: string, body: Child, okText = "Đóng"): Promise<void> {
  return new Promise((resolve) => {
    const wrap = h("div", { class: "modal-wrap", role: "dialog", "aria-modal": "true", "data-testid": "info" },
      h("div", { class: "modal" }, h("h3", {}, title), body,
        h("div", { class: "row end" }, h("button", { type: "button", class: "pri", title: "Đóng hộp thông tin này", "data-testid": "info-dong", onclick: () => { wrap.remove(); resolve(); } }, okText))));
    wrap.addEventListener("dong-hop", () => resolve());
    document.body.appendChild(wrap);
  });
}

/**
 * L-82: đóng hộp thoại của TRANG (mọi .modal-wrap trừ cửa sổ thông báo toàn cục) — khi đổi trang (Back / F5 hash / bấm liên kết) hộp của
 * trang cũ không được ở lại che trang mới; `chiTrenCung` = chỉ hộp trên cùng (phím Esc). Hộp nghe sự kiện "dong-hop" để kết thúc lời hứa.
 */
export function dongHopTrang(chiTrenCung = false): number {
  const ds = [...document.querySelectorAll<HTMLElement>(".modal-wrap")].filter((w) => w.dataset.testid !== "notice-popup");
  const chon = chiTrenCung ? ds.slice(-1) : ds;
  for (const w of chon) { w.dispatchEvent(new Event("dong-hop")); w.remove(); }
  return chon.length;
}

/** Khoá nút trong lúc await (bấm 2 lần = 1 request). */
export async function busy<T>(btn: HTMLButtonElement, textBusy: string, fn: () => Promise<T>): Promise<T | undefined> {
  if (btn.disabled) return undefined;
  const old = btn.textContent;
  // Giữ NGUYÊN bề rộng khi đổi chữ thành "…": nút co hẹp thì nút kế bên (vd. "Giải CAPTCHA") trượt vào đúng chỗ con trỏ và cú bấm thứ hai của
  // bấm đúp rơi trúng nó (bố cục thẻ bot A3 làm lộ lỗi này — E2E MH4 / N8). Trả lại như cũ khi xong.
  const minCu = btn.style.minWidth;
  const rong = btn.getBoundingClientRect().width;
  if (rong > 0) btn.style.minWidth = `${Math.ceil(rong)}px`;
  btn.disabled = true;
  btn.textContent = textBusy;
  try {
    return await fn();
  } finally {
    btn.disabled = false;
    btn.textContent = old;
    btn.style.minWidth = minCu;
  }
}

export function debounce<A extends unknown[]>(ms: number, fn: (...a: A) => void): (...a: A) => void {
  let t: number | undefined;
  return (...a: A) => { clearTimeout(t); t = window.setTimeout(() => fn(...a), ms); };
}
